import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ProfileImages, CommonImages } from "../../../../reusableUI/images";
import moment from "moment";
import { FaRegUser } from "react-icons/fa6";
import { FaUserNurse } from "react-icons/fa6";
import { MdOutlineCardTravel } from "react-icons/md";
import { AiTwotoneCheckCircle } from "react-icons/ai";
import {
  capitalizeFirstLetter,
  formatPhoneNumber,
} from "../../../../utils/Functions";

const BookingsProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let { user, card } = location.state || {};

  const summaryId = String(user?.visitInfo?.dischargeSummary || "").trim();
  const dischargeSummaryDoc =
    user?.visitInfo?.dischargeSummaryDoc ||
    (summaryId && Array.isArray(user?.patientID?.dischargeSummaries)
      ? user.patientID.dischargeSummaries.find(
          (item) => String(item?._id || "") === summaryId
        ) || null
      : null);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F3F6F5]">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <p className="text-[18px] font-semibold text-gray-700 font-poppins">
            Profile not found
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-[#4951EC] text-white rounded-lg hover:bg-[#3941cc] transition-colors font-poppins"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F3F6F5] min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10 mx-2">
        <div className="flex items-center px-4 py-3">
          <img
            src={ProfileImages.prev}
            className="h-5 w-5 cursor-pointer hover:opacity-70 transition-opacity"
            onClick={() => {
              navigate(-1);
              localStorage.setItem("bookingCard", card);
            }}
            alt="Back"
          />
          <p className="text-[#4951EC] ml-3 text-[16px] font-semibold font-poppins">
            Booking Details
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="overflow-y-auto h-[calc(100vh-60px)] pb-8">
        <div className="max-w-8xl  px-2 py-2">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-10">
            {/* Cover Image and Profile Picture */}
            <div className="relative">
              <img
                src={ProfileImages.cover1}
                className="h-20 w-full object-cover"
                alt="Cover"
              />
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                <img
                  src={CommonImages.CommonDp}
                  className="h-16 w-16 rounded-full border-4 border-white shadow-lg object-cover"
                  alt="Profile"
                />
              </div>
            </div>

            {/* Patient Name Section */}
            <div className="pt-10 pb-4 text-center border-b border-gray-200">
              <h1 className="text-[16px] font-bold text-gray-800 font-poppins">
                {user?.patient?.name || "N/A"}
              </h1>
              <p className="text-[12px] text-gray-500 font-poppins mt-1">
                Booking ID: {user?.visitInfo?.jobID || "N/A"}
              </p>
            </div>

            {/* Patient Info */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                  <FaRegUser size={14} color="#4951EC" />
                </div>
                <h2 className="text-[#4951EC] ml-3 text-[14px] font-semibold font-poppins">
                  Patient Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-2">
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                    Name:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {user?.patient?.name || "N/A"}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                    Gender:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {capitalizeFirstLetter(user?.patient?.gender) || "N/A"}
                  </span>
                </div>
                {user?.patient?.userID?.personalInfo?.basicInfo?.otherInfo
                  ?.dob && (
                  <div className="flex items-center">
                    <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                      Age:
                    </span>
                    <span className="text-[13px] text-gray-600 font-poppins">
                      {moment().diff(
                        moment(
                          user?.patient?.userID?.personalInfo?.basicInfo
                            ?.otherInfo?.dob
                        ),
                        "years"
                      )}{" "}
                      years
                    </span>
                  </div>
                )}
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                    Contact:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {formatPhoneNumber(
                      user?.patientID?.personalInfo?.basicInfo?.otherInfo?.phone
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Specialist Info */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                  <FaUserNurse size={14} color="#4951EC" />
                </div>
                <h2 className="text-[#4951EC] ml-3 text-[14px] font-semibold font-poppins">
                  Specialist Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-2">
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                    Name:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {user?.specialistInfo?.name || "Not Assigned"}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-24">
                    Role:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {capitalizeFirstLetter(user?.specialistInfo?.subRole) ||
                      "Not Assigned"}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                  <MdOutlineCardTravel size={14} color="#4951EC" />
                </div>
                <h2 className="text-[#4951EC] ml-3 text-[14px] font-semibold font-poppins">
                  Visit Information
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ml-2">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Visit Date:
                    </p>
                    <p className="text-[13px] text-gray-600 font-poppins">
                      {moment(user?.visitInfo?.startDate).format("DD-MM-YYYY")}{" "}
                      to {moment(user?.visitInfo?.endDate).format("DD-MM-YYYY")}
                    </p>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Visit Time:
                    </p>
                    <p className="text-[13px] text-gray-600 font-poppins">
                      {user?.visitInfo?.slot || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Duty Type:
                    </p>
                    <p className="text-[13px] text-gray-600 font-poppins">
                      {capitalizeFirstLetter(user?.dutyType) || "N/A"}
                    </p>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Status:
                    </p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-[12px] font-medium font-poppins ${
                        user?.status?.toLowerCase() === "completed"
                          ? "bg-green-100 text-green-700"
                          : user?.status?.toLowerCase() === "pending"
                          ? "bg-orange-100 text-orange-700"
                          : user?.status?.toLowerCase() === "confirmed"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {capitalizeFirstLetter(user?.status) || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Address:
                    </p>
                    <p className="text-[13px] text-gray-600 font-poppins">
                      {user?.visitInfo?.addressInfo?.address1}
                      {user?.visitInfo?.addressInfo?.address2 &&
                        `, ${user?.visitInfo?.addressInfo?.address2}`}
                      {", "}
                      {user?.visitInfo?.addressInfo?.city}
                      {", "}
                      {user?.visitInfo?.addressInfo?.state} -{" "}
                      {user?.visitInfo?.addressInfo?.pincode}
                    </p>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Services:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {user?.visitInfo?.services?.map((service, index) => (
                        <span
                          key={index}
                          className="inline-block px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-[12px] font-poppins"
                        >
                          {capitalizeFirstLetter(service)}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Discharge Summary:
                    </p>
                    {dischargeSummaryDoc?.key ? (
                      <a
                        href={dischargeSummaryDoc.key}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[13px] text-blue-600 hover:underline font-poppins break-words"
                      >
                        {dischargeSummaryDoc?.title || "View Summary"}
                      </a>
                    ) : (
                      <p className="text-[13px] text-gray-600 font-poppins break-words">
                        {dischargeSummaryDoc?.title ||
                          user?.visitInfo?.dischargeSummary ||
                          "N/A"}
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-[13px] font-medium text-gray-700 font-poppins mb-1">
                      Fees:
                    </p>
                    <p className="text-[13px] text-gray-600 font-poppins">
                      ₹ {user?.paymentInfo?.assessment?.fee || "N/A"} per day
                    </p>
                    {user?.paymentInfo?.totalAmount && (
                      <p className="text-[12px] text-gray-500 font-poppins mt-1">
                        Total: ₹ {user?.paymentInfo?.totalAmount}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Accommodation and Food */}
            <div className="p-6">
              <h2 className="text-[#4951EC] mb-4 text-[14px] font-semibold font-poppins">
                Accommodation and Food
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 ml-2 pb-4">
                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-28">
                    Pick Up:
                  </span>
                  <div className="flex items-center gap-2">
                    {user?.visitInfo?.travelAllowance?.find(
                      (item) => item.label === "Pick Up"
                    )?.opted ? (
                      <>
                        <AiTwotoneCheckCircle
                          size={18}
                          className="text-green-500"
                        />
                        <span className="text-[13px] text-green-600 font-medium font-poppins">
                          Yes
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-[13px] text-red-600 font-medium font-poppins">
                          No
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-28">
                    Drop:
                  </span>
                  <div className="flex items-center gap-2">
                    {user?.visitInfo?.travelAllowance?.find(
                      (item) => item.label === "Drop"
                    )?.opted ? (
                      <>
                        <AiTwotoneCheckCircle
                          size={18}
                          className="text-green-500"
                        />
                        <span className="text-[13px] text-green-600 font-medium font-poppins">
                          Yes
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-[13px] text-red-600 font-medium font-poppins">
                          No
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <span className="text-[13px] font-medium text-gray-700 font-poppins w-28">
                    Food:
                  </span>
                  <span className="text-[13px] text-gray-600 font-poppins">
                    {user?.visitInfo?.foodAccommodation?.[0]?.label ||
                      "Not Provided"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingsProfile;
